import { collectByClaimIds, DEFAULT_MAX_CLAIMS, ICollectByClaimIdsProgress } from '../../utils/collect-by-claim-ids';
import { RateLimiter } from '../../utils/rate-limiter';
import { FilterMethod, ICustomFilter } from '../data-table/components/filters/filters.models';
import { buildClaimIdFilters } from './patient-claim-search';
import { buildClaimFromDosValue, CLAIM_DOS_FILTER_ID, readClaimDosRange } from './task-search';

/**
 * Task filters GET /message cannot express, answered through the claims they select instead
 * (CA-3441). All are sentinels: they never reach GET /message as themselves.
 *
 * - DOS: `filterFromDos` on GET /claim. /message ignores every DOS param name (measured
 *   2026-10-06, provider and support).
 * - Payer: `filterPayerId` on GET /claim. /message accepts `filterPayerId` but matches only a
 *   direct PAYER link, which no task carries (0/919 provider, 0/1,413 support) — 0 for every payer.
 * - Patient: `filterPatientId` on GET /claim — the same lookup the Patient search always made,
 *   which used to send every claim ID it found in one /message URL.
 * - Coder rule: `filterCodingRuleNumber` on GET /claim, sent raw (`EQ:12` answers 500, FE-126).
 * - Claim IDs: an explicit list, for a search whose claims do not come from GET /claim (Coding
 *   result, from GET /coding). Without a GET /claim param it is the claim source; with one, it
 *   narrows each chunk.
 *
 * Several payers OR; payer and DOS AND (measured on GET /provider/claim 2026-10-08). Several
 * patients OR (the Patient search has always sent them that way).
 */
export const CLAIM_PAYER_FILTER_ID = 'filterClaimPayerId';
export const CLAIM_PATIENT_FILTER_ID = 'filterClaimPatientId';
export const CLAIM_CODER_RULE_FILTER_ID = 'filterClaimCodingRuleNumber';
export const CLAIM_ID_SCOPE_FILTER_ID = 'filterClaimScopeId';
export const CLAIM_SCOPE_FILTER_IDS: readonly string[] = [
    CLAIM_DOS_FILTER_ID,
    CLAIM_PAYER_FILTER_ID,
    CLAIM_PATIENT_FILTER_ID,
    CLAIM_CODER_RULE_FILTER_ID,
    CLAIM_ID_SCOPE_FILTER_ID,
];

/** The GET /claim params a claim scope sends. */
export interface IClaimScopeQuery {
    filterFromDos?: string[];
    filterPayerId?: string[];
    filterPatientId?: string[];
    filterCodingRuleNumber?: number;
}

export interface IClaimScopeStatus {
    claimsLoaded: number;
    claimsTotal: number;
    claimsCapped: boolean;
    done: boolean;
    /** The collection stopped on an error — the list holds only what arrived before it. */
    failed: boolean;
}

/** Minimal message shape the adapter sorts and filters on — Provider and Support messages both fit. */
export interface IClaimScopedMessage {
    id?: string;
    open?: boolean;
    read?: boolean;
    subject?: string;
    purposeType?: string;
    priority?: string;
    created?: Date | string;
    updated?: Date | string;
    dueDate?: Date | string;
    links?: {
        users?: Array<{ id?: string }>;
        messageCategories?: Array<{ id?: string }>;
    };
}

export interface IClaimScopedListingRequest {
    page?: number;
    pageSize?: number;
    sort?: string[];
    order?: string[];
    filters?: ICustomFilter[];
}

export interface IClaimScopedListing<T> {
    messages: T[];
    total: number;
    status: IClaimScopeStatus;
}

export interface IClaimScopedListingDeps<T> {
    /** One page of GET /claim with the scope's params (plus any app-level params, e.g. support's filterActive). */
    fetchClaimPage: (scope: IClaimScopeQuery, page: number, pageSize: number) => Promise<{ ids: string[]; total: number }>;
    /** One page of GET /message. Its filters never contain a claim-scope sentinel. */
    fetchMessagePage: (request: IClaimScopedListingRequest) => Promise<{ messages: T[]; total: number }>;
}

const optionValue = (raw: unknown): string => {
    if (raw && typeof raw === 'object' && 'value' in raw) {
        return String((raw as { value?: unknown }).value ?? '').trim();
    }
    return raw == null ? '' : String(raw).trim();
};

const filterValues = (filter: ICustomFilter): string[] =>
    (Array.isArray(filter.value) ? filter.value : [filter.value]).map(optionValue).filter(Boolean);

export function hasClaimScope(filters?: ICustomFilter[]): boolean {
    return readClaimScope(filters) !== null || readClaimIdScope(filters) !== null;
}

const sentinelValues = (filters: ICustomFilter[] | undefined, filterId: string): string[] =>
    [...new Set((filters || []).filter(f => f.filterId === filterId && f.filterMethod === FilterMethod.EQ).flatMap(filterValues))];

/** The GET /claim query the sentinels in `filters` describe, or null when there are none. */
export function readClaimScope(filters?: ICustomFilter[]): IClaimScopeQuery | null {
    const scope: IClaimScopeQuery = {};
    const fromDos = buildClaimFromDosValue(readClaimDosRange(filters));
    if (fromDos) {
        scope.filterFromDos = [fromDos];
    }
    const payerIds = sentinelValues(filters, CLAIM_PAYER_FILTER_ID);
    if (payerIds.length) {
        scope.filterPayerId = payerIds.sort().map(id => `EQ:${id}`);
    }
    const patientIds = sentinelValues(filters, CLAIM_PATIENT_FILTER_ID);
    if (patientIds.length) {
        scope.filterPatientId = patientIds.sort().map(id => `EQ:${id}`);
    }
    const ruleNumber = sentinelValues(filters, CLAIM_CODER_RULE_FILTER_ID).map(Number).find(Number.isInteger);
    if (ruleNumber !== undefined) {
        scope.filterCodingRuleNumber = ruleNumber;
    }
    return Object.keys(scope).length ? scope : null;
}

/** The explicit claim IDs in `filters` (sorted, unique), or null when there are none. */
export function readClaimIdScope(filters?: ICustomFilter[]): string[] | null {
    const ids = sentinelValues(filters, CLAIM_ID_SCOPE_FILTER_ID);
    return ids.length ? ids.sort() : null;
}

export function buildClaimIdScopeFilters(claimIds: string[]): ICustomFilter[] {
    return claimIds.map(id => ({
        filterId: CLAIM_ID_SCOPE_FILTER_ID,
        filterMethod: FilterMethod.EQ,
        filterType: 'String',
        value: id,
    }));
}

export function withoutClaimScope(filters?: ICustomFilter[]): ICustomFilter[] {
    return (filters || []).filter(f => !CLAIM_SCOPE_FILTER_IDS.includes(f.filterId));
}

// ── filters evaluated on the collected set ──────────────────────────────────────────────────────
//
// One collection serves the open and done lists, every chip count, Mine and the Unread count:
// it is fetched WITHOUT these filters, and they are applied here instead. Each was measured
// against the server on 2026-10-08 (provider 919 and support 1,426 tasks, exact match):
// filterOpen ↔ `open`, filterRead ↔ `read` (not support's providerOpen/providerRead),
// filterUserId ↔ links.users, filterMessageCategoryId ↔ links.messageCategories, repeated
// values of one filter OR.
//
// filterId ↔ `id` is applied here too, for a different reason: the Body search resolves to up to
// 100 message IDs, and left server-side they would ride on every chunk request next to its 100
// claim IDs — past CloudFront's URL limit (414 at ~184 UUIDs). It is an exact match on the ID,
// and Body search already depends on repeated `filterId` values OR-ing.

type LocalMatcher = (message: IClaimScopedMessage, value: string) => boolean;

const LOCAL_MATCHERS: Record<string, LocalMatcher> = {
    filterOpen: (m, v) => m.open === (v === '1'),
    filterRead: (m, v) => m.read === (v === '1'),
    filterUserId: (m, v) => (m.links?.users || []).some(u => u.id === v),
    filterMessageCategoryId: (m, v) => (m.links?.messageCategories || []).some(c => c.id === v),
    filterId: (m, v) => m.id === v,
};

const isLocalFilter = (filter: ICustomFilter): boolean =>
    filter.filterMethod === FilterMethod.EQ && filter.filterId in LOCAL_MATCHERS;

/** Exported for tests. AND across filter ids, OR across the values of one id. */
export function matchesLocalFilters(message: IClaimScopedMessage, filters: ICustomFilter[]): boolean {
    const byId = new Map<string, string[]>();
    filters.forEach(f => byId.set(f.filterId, [...(byId.get(f.filterId) || []), ...filterValues(f)]));
    return [...byId.entries()].every(([filterId, values]) => values.some(v => LOCAL_MATCHERS[filterId](message, v)));
}

// ── sort, applied to the merged set (each chunk only arrives sorted within itself) ──────────────

const PRIORITY_RANK: Record<string, number> = { LOW: 0, NORMAL: 1, HIGH: 2, URGENT: 3 };

const sortValue = (message: IClaimScopedMessage, sort: string): number | string | undefined => {
    switch (sort) {
        case 'CREATED':
            return message.created ? new Date(message.created).getTime() : undefined;
        case 'UPDATED':
            return message.updated ? new Date(message.updated).getTime() : undefined;
        case 'DUE_DATE':
            return message.dueDate ? new Date(message.dueDate).getTime() : undefined;
        case 'SUBJECT':
            return message.subject?.toLowerCase();
        case 'PURPOSE':
            return message.purposeType?.toLowerCase();
        case 'PRIORITY':
            return message.priority ? PRIORITY_RANK[message.priority] : undefined;
        case 'OPEN':
            return message.open === undefined ? undefined : Number(message.open);
        case 'READ':
            return message.read === undefined ? undefined : Number(message.read);
        default:
            return undefined;
    }
};

/**
 * Exported for tests. Sorts by every rule in order — the task table allows shift-click multi-sort and
 * the normal `/message` path sends every rule, so a secondary rule must not be dropped here.
 * Within each rule missing values sort last either way; full ties keep arrival order.
 */
export function sortClaimScopedMessages<T extends IClaimScopedMessage>(
    messages: T[],
    sorts: string[] = [],
    orders: string[] = [],
): T[] {
    const rules = (sorts.length ? sorts : ['UPDATED']).map((sort, i) => ({
        sort,
        // A rule without an order is descending, as before multi-sort.
        direction: /^asc/i.test(orders[i] ?? 'DESCENDING') ? 1 : -1,
    }));
    const compareRule = (a: number | string | undefined, b: number | string | undefined, direction: number): number => {
        if (a === undefined || b === undefined) {
            return a === b ? 0 : a === undefined ? 1 : -1;
        }
        return a === b ? 0 : (a < b ? -1 : 1) * direction;
    };
    return messages
        .map((message, index) => ({ message, index, values: rules.map(rule => sortValue(message, rule.sort)) }))
        .sort((a, b) => {
            for (let i = 0; i < rules.length; i++) {
                const result = compareRule(a.values[i], b.values[i], rules[i].direction);
                if (result !== 0) {
                    return result;
                }
            }
            return a.index - b.index;
        })
        .map(entry => entry.message);
}

// ── one collection at a time, shared by the list and its counts ─────────────────────────────────

interface ICollectionEntry {
    key: string;
    controller: AbortController;
    promise: Promise<ICollectByClaimIdsProgress<IClaimScopedMessage>>;
    latest: ICollectByClaimIdsProgress<IClaimScopedMessage>;
    failed: boolean;
    listeners: Set<(progress: ICollectByClaimIdsProgress<IClaimScopedMessage>) => void>;
}

/** One budget for every claim-scoped request. Sandbox answers 429 to unthrottled bursts. */
let rateLimiter = new RateLimiter({ maxRequests: 100, windowMs: 60_000 });

/** Tests only: a limiter that does not make a suite wait out a minute. */
export function setClaimScopeRateLimiterForTests(limiter: RateLimiter): void {
    rateLimiter = limiter;
}

/** The same budget, for requests made before a collection starts (the Coding result's GET /coding pages). */
export function getClaimScopeRateLimiter(): RateLimiter {
    return rateLimiter;
}
const CLAIM_PAGE_TTL_MS = 2 * 60_000;

let current: ICollectionEntry | null = null;
let claimPageCache: { key: string; at: number; pages: Map<string, Promise<{ ids: string[]; total: number }>> } | null = null;
const statusListeners = new Set<(status: IClaimScopeStatus | null) => void>();

const statusOf = (entry: ICollectionEntry | null): IClaimScopeStatus | null =>
    entry
        ? {
              claimsLoaded: entry.latest.claimsLoaded,
              claimsTotal: entry.latest.claimsTotal,
              claimsCapped: entry.latest.claimsCapped,
              done: entry.latest.done,
              failed: entry.failed,
          }
        : null;

const emitStatus = (): void => statusListeners.forEach(listener => listener(statusOf(current)));

/** The latest claim-scoped collection — for "loading 300 of 1,200 claims" / "first 5,000 claims only". */
export function getClaimScopeStatus(): IClaimScopeStatus | null {
    return statusOf(current);
}

export function subscribeClaimScopeStatus(listener: (status: IClaimScopeStatus | null) => void): () => void {
    statusListeners.add(listener);
    return () => statusListeners.delete(listener);
}

/** Abort and forget the current collection, e.g. when the claim filters are cleared. */
export function clearClaimScopedListing(): void {
    current?.controller.abort();
    current = null;
    claimPageCache = null;
    emitStatus();
}

const keyOf = (value: unknown): string => JSON.stringify(value);

const serverFiltersKey = (filters: ICustomFilter[]): string =>
    filters
        .map(f => `${f.filterId}:${f.filterMethod}:${filterValues(f).join(',')}`)
        .sort()
        .join('|');

/** One page of an explicit claim-ID list, shaped like a GET /claim page. */
const claimIdListPage = (claimIds: string[], page: number, pageSize: number): Promise<{ ids: string[]; total: number }> =>
    Promise.resolve({ ids: claimIds.slice(page * pageSize, (page + 1) * pageSize), total: claimIds.length });

function startCollection<T extends IClaimScopedMessage>(
    key: string,
    scope: IClaimScopeQuery | null,
    claimIdScope: string[] | null,
    serverFilters: ICustomFilter[],
    deps: IClaimScopedListingDeps<T>,
    maxClaims: number,
): ICollectionEntry {
    current?.controller.abort();

    const scopeKey = keyOf(scope);
    if (!claimPageCache || claimPageCache.key !== scopeKey || Date.now() - claimPageCache.at > CLAIM_PAGE_TTL_MS) {
        claimPageCache = { key: scopeKey, at: Date.now(), pages: new Map() };
    }
    const pages = claimPageCache.pages;

    // A claim-ID list (Coding result) and any `filterClaimId` already in the request each narrow
    // the claims; intersect every chunk with all of them — sending them alongside the chunk would
    // OR them and widen the list. Without a GET /claim query the list itself is the claim source.
    const narrowing = [
        ...(scope && claimIdScope ? [new Set(claimIdScope)] : []),
        ...(serverFilters.some(f => f.filterId === 'filterClaimId')
            ? [new Set(serverFilters.filter(f => f.filterId === 'filterClaimId').flatMap(filterValues))]
            : []),
    ];
    const baseFilters = serverFilters.filter(f => f.filterId !== 'filterClaimId');

    const controller = new AbortController();
    const entry: ICollectionEntry = {
        key,
        controller,
        latest: { items: [], claimsLoaded: 0, claimsTotal: 0, claimsCapped: false, done: false },
        failed: false,
        listeners: new Set(),
        promise: Promise.resolve(undefined as never),
    };

    entry.promise = collectByClaimIds<IClaimScopedMessage>({
        maxClaims,
        rateLimiter,
        signal: controller.signal,
        fetchClaimPage: (page, pageSize) => {
            if (!scope) {
                return claimIdListPage(claimIdScope || [], page, pageSize);
            }
            const pageKey = `${page}:${pageSize}`;
            let pending = pages.get(pageKey);
            if (!pending) {
                pending = deps.fetchClaimPage(scope, page, pageSize);
                pages.set(pageKey, pending);
                pending.catch(() => pages.delete(pageKey));
            }
            return pending;
        },
        fetchItemPage: async (claimIds, page, pageSize) => {
            const chunk = claimIds.filter(id => narrowing.every(set => set.has(id)));
            if (!chunk.length) {
                return { items: [], total: 0 };
            }
            const listing = await deps.fetchMessagePage({
                page,
                pageSize,
                filters: [...baseFilters, ...buildClaimIdFilters(chunk)],
            });
            return { items: listing.messages, total: listing.total };
        },
        onProgress: progress => {
            if (current !== entry) {
                return;
            }
            entry.latest = progress;
            entry.listeners.forEach(listener => listener(progress));
            emitStatus();
        },
    }).catch(error => {
        if (current === entry) {
            entry.failed = !controller.signal.aborted;
            emitStatus();
        }
        throw error;
    });

    // Awaiters see the rejection; this only keeps an abandoned (aborted) collection from
    // surfacing as an unhandled rejection.
    entry.promise.catch(() => undefined);

    current = entry;
    emitStatus();
    return entry;
}

function project<T extends IClaimScopedMessage>(
    progress: ICollectByClaimIdsProgress<IClaimScopedMessage>,
    request: IClaimScopedListingRequest,
    localFilters: ICustomFilter[],
    entry: ICollectionEntry,
): IClaimScopedListing<T> {
    const matching = progress.items.filter(m => matchesLocalFilters(m, localFilters)) as T[];
    const sorted = sortClaimScopedMessages(matching, request.sort, request.order);
    const pageSize = request.pageSize ?? (sorted.length || 1);
    const start = (request.page ?? 0) * pageSize;
    return {
        messages: sorted.slice(start, start + pageSize),
        total: sorted.length,
        status: statusOf(entry) as IClaimScopeStatus,
    };
}

/**
 * GET /message for a request carrying claim-scope filters: collect every task on the scope's
 * claims (see {@link collectByClaimIds}), then filter, sort and page that set here.
 *
 * Requests that differ only in open / read / assignee / category / page / sort share one
 * collection, so the list, its chip counts and the Unread count cost one fan-out. A first-page
 * listing (`fresh`) recollects so a refresh after an edit is not served stale; later pages and
 * counts reuse the current collection, and anything already in flight is joined, not restarted.
 *
 * `onPartial` receives the requested page each time another claim chunk lands.
 */
export async function fetchClaimScopedListing<T extends IClaimScopedMessage>(
    request: IClaimScopedListingRequest,
    deps: IClaimScopedListingDeps<T>,
    options?: { fresh?: boolean; onPartial?: (listing: IClaimScopedListing<T>) => void; maxClaims?: number },
): Promise<IClaimScopedListing<T>> {
    const scope = readClaimScope(request.filters);
    const claimIdScope = readClaimIdScope(request.filters);
    if (!scope && !claimIdScope) {
        throw new Error('fetchClaimScopedListing needs a claim-scope filter');
    }
    const messageFilters = withoutClaimScope(request.filters);
    const localFilters = messageFilters.filter(isLocalFilter);
    const serverFilters = messageFilters.filter(f => !isLocalFilter(f));
    const maxClaims = options?.maxClaims ?? DEFAULT_MAX_CLAIMS;
    const key = keyOf([scope, claimIdScope?.join(','), serverFiltersKey(serverFilters), maxClaims]);

    const reusable = current && current.key === key && !current.failed && (!options?.fresh || !current.latest.done);
    const entry = reusable
        ? (current as ICollectionEntry)
        : startCollection(key, scope, claimIdScope, serverFilters, deps, maxClaims);

    const onPartial = options?.onPartial;
    const listener = onPartial
        ? (progress: ICollectByClaimIdsProgress<IClaimScopedMessage>) =>
              onPartial(project<T>(progress, request, localFilters, entry))
        : null;
    if (listener) {
        entry.listeners.add(listener);
        if (entry.latest.claimsLoaded > 0) {
            listener(entry.latest);
        }
    }
    try {
        const result = await entry.promise;
        return project<T>(result, request, localFilters, entry);
    } finally {
        if (listener) {
            entry.listeners.delete(listener);
        }
    }
}

/** Count for a claim-scoped request — the total of {@link fetchClaimScopedListing}, from the shared collection. */
export async function countClaimScopedMessages<T extends IClaimScopedMessage>(
    request: IClaimScopedListingRequest,
    deps: IClaimScopedListingDeps<T>,
): Promise<number> {
    const listing = await fetchClaimScopedListing<T>({ ...request, page: 0, pageSize: 1 }, deps);
    return listing.total;
}
